<template>
  <div class="generate-reports-container">
    <!-- Step 1: Report Selection -->
    <div v-if="!selectedReport">
      <h4 class="section-title mb-2">
        <feather-icon icon="FileTextIcon" class="mr-50" />
        Select Report to Generate
      </h4>
      <b-row class="export-cards-grid">
        <!-- MRM Report -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div class="export-card" @click="selectReport('exportMrm')">
            <div class="export-card-icon mrm">
              <feather-icon icon="ClipboardIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>MRM Report</h4>
              <p>Medical Record Management data export</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>

        <!-- Approval Tracking -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div
            class="export-card"
            @click="selectReport('exportApprovalTracking')"
          >
            <div class="export-card-icon approval">
              <feather-icon icon="CheckCircleIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>Approval Tracking</h4>
              <p>Track approval statuses and workflows</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>

        <!-- Invoice Report -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div class="export-card" @click="selectReport('exportInvoiceReport')">
            <div class="export-card-icon invoice">
              <feather-icon icon="FileTextIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>Invoice Report</h4>
              <p>Financial and billing data export</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>

        <!-- Collected Report -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div
            class="export-card"
            @click="selectReport('exportCollectedReport')"
          >
            <div class="export-card-icon collected">
              <feather-icon icon="DatabaseIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>Collected Report</h4>
              <p>Collected data and statistics export</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>

        <!-- Trending Report -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div
            class="export-card"
            @click="selectReport('exportTrendingReport')"
          >
            <div class="export-card-icon trending">
              <feather-icon icon="TrendingUpIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>Trending Report</h4>
              <p>Trends and analytics data export</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>

        <!-- Discharge Summary Report -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div
            class="export-card"
            @click="selectReport('exportDischargeSummary')"
          >
            <div class="export-card-icon discharge">
              <feather-icon icon="ActivityIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>Discharge Summary</h4>
              <p>Hospital discharge summary data export</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>

        <!-- EMR Report -->
        <b-col cols="12" md="6" lg="4" class="mb-2">
          <div
            class="export-card"
            @click="selectReport('exportEmrReport')"
          >
            <div class="export-card-icon emr">
              <feather-icon icon="MonitorIcon" size="24" />
            </div>
            <div class="export-card-content">
              <h4>EMR Report</h4>
              <p>Electronic Medical Records data export</p>
            </div>
            <div class="export-card-action">
              <feather-icon icon="ChevronRightIcon" size="18" />
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Step 2: Filters Section -->
    <div v-else>
      <!-- Selected Report Card -->
      <div class="selected-report-card mb-2">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <div :class="['report-badge', getReportClass(selectedReport)]">
              <feather-icon :icon="getReportIcon(selectedReport)" size="20" />
            </div>
            <div class="ml-1">
              <h5 class="mb-0">{{ getReportTitle(selectedReport) }}</h5>
              <small class="text-muted"
                >Configure filters and generate report</small
              >
            </div>
          </div>
          <b-button variant="outline-secondary" size="sm" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" class="mr-50" />
            Back
          </b-button>
        </div>
      </div>

      <!-- Dynamic Filters Card -->
      <b-card class="filters-card mb-2">
        <b-card-header class="pb-1">
          <div class="d-flex align-items-center">
            <feather-icon
              icon="FilterIcon"
              size="18"
              class="mr-1 text-primary"
            />
            <h5 class="mb-0">Filter Options</h5>
          </div>
        </b-card-header>
        <b-card-body class="pt-1">
          <b-row>
            <!-- ============ MRM Report Filters ============ -->
            <template v-if="selectedReport === 'exportMrm'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Doctor</label>
                <v-select
                  v-model="filter.dr_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.doctors ? lookups.doctors : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Doctor"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>

            <!-- ============ Approval Tracking Filters ============ -->
            <template v-if="selectedReport === 'exportApprovalTracking'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Doctor</label>
                <v-select
                  v-model="filter.dr_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.doctors ? lookups.doctors : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Doctor"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Insurance Code</label>
                <b-form-input
                  v-model="filter.insurance_code"
                  placeholder="Enter Insurance Code"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>

            <!-- ============ Invoice Report Filters ============ -->
            <template v-if="selectedReport === 'exportInvoiceReport'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Status</label>
                <v-select
                  v-model="filter.status_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="statusOptions"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Status"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Doctor</label>
                <v-select
                  v-model="filter.dr_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.doctors ? lookups.doctors : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Doctor"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Specialty</label>
                <b-form-input
                  v-model="filter.specialty"
                  placeholder="Enter Specialty"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Service Code</label>
                <b-form-input
                  v-model="filter.service_code"
                  placeholder="Enter Service Code"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>

            <!-- ============ Collected Report Filters ============ -->
            <template v-if="selectedReport === 'exportCollectedReport'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Auditor</label>
                <v-select
                  v-model="filter.auditor_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="
                    lookups && lookups.reviewers ? lookups.reviewers : []
                  "
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Auditor"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">Doctor</label>
                <v-select
                  v-model="filter.dr_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.doctors ? lookups.doctors : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Doctor"
                />
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>

            <!-- ============ Trending Report Filters ============ -->
            <template v-if="selectedReport === 'exportTrendingReport'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>

            <!-- ============ Discharge Summary Report Filters ============ -->
            <template v-if="selectedReport === 'exportDischargeSummary'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>

            <!-- ============ EMR Report Filters ============ -->
            <template v-if="selectedReport === 'exportEmrReport'">
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Branch <span class="text-danger">*</span>
                </label>
                <v-select
                  v-model="filter.branch_id"
                  :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                  :options="lookups && lookups.branches ? lookups.branches : []"
                  label="name"
                  class="w-100"
                  :reduce="(val) => val.id"
                  placeholder="Select Branch"
                  :class="{ 'is-invalid': !filter.branch_id && showValidation }"
                />
                <small
                  v-if="!filter.branch_id && showValidation"
                  class="text-danger"
                >
                  Branch is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  Start Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.start_date"
                  placeholder="Select Start Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{
                    'is-invalid': !filter.start_date && showValidation,
                  }"
                />
                <small
                  v-if="!filter.start_date && showValidation"
                  class="text-danger"
                >
                  Start Date is required
                </small>
              </b-col>
              <b-col cols="12" md="4" class="mb-1">
                <label class="form-label">
                  End Date <span class="text-danger">*</span>
                </label>
                <b-form-datepicker
                  v-model="filter.end_date"
                  placeholder="Select End Date"
                  reset-button
                  :dropup="false"
                  no-flip
                  :class="{ 'is-invalid': !filter.end_date && showValidation }"
                />
                <small
                  v-if="!filter.end_date && showValidation"
                  class="text-danger"
                >
                  End Date is required
                </small>
              </b-col>
            </template>
          </b-row>

          <!-- Action Buttons -->
          <b-row class="mt-2">
            <b-col cols="12" class="d-flex justify-content-end">
              <b-button
                variant="outline-secondary"
                class="mr-1"
                @click="resetFilters"
              >
                <feather-icon icon="RefreshCwIcon" class="mr-50" />
                Reset Filters
              </b-button>
              <b-button
                variant="primary"
                @click="exportReport"
                :disabled="loading"
              >
                <b-spinner v-if="loading" small class="mr-50" />
                <feather-icon v-else icon="DownloadIcon" class="mr-50" />
                Generate Report
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </div>

    <!-- Progress Modal -->
    <b-modal
      id="export-progress-modal"
      title="Generating Report..."
      v-model="showProgressModal"
      hide-footer
      hide-header-close
      centered
      no-close-on-backdrop
      no-close-on-esc
    >
      <div class="export-progress-content">
        <div class="export-progress-icon">
          <b-spinner variant="primary" />
        </div>
        <p class="mb-2">Generating your report, please wait...</p>
        <b-progress height="24px" :value="progress" animated>
          <b-progress-bar :value="progress" variant="primary">
            {{ progress }}%
          </b-progress-bar>
        </b-progress>
        <div class="mt-2">
          <b-button variant="outline-danger" size="sm" @click="cancelExport">
            Cancel
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormDatepicker,
  BFormInput,
  BModal,
  BProgress,
  BProgressBar,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapGetters } from "vuex";
import { CancelToken } from "@/utils/request";

export default {
  name: "ExportReports",
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormDatepicker,
    BFormInput,
    BModal,
    BProgress,
    BProgressBar,
    BSpinner,
    vSelect,
  },
  data() {
    return {
      selectedReport: null,
      progress: 0,
      showProgressModal: false,
      loading: false,
      showValidation: false,
      filter: {},
      cancelSource: null,
      statusOptions: [
        { id: 0, name: "New" },
        { id: 1, name: "Waiting branch feedback" },
        { id: 2, name: "Updated" },
        { id: 3, name: "Closed" },
      ],
      patientTypes: [
        { id: "I", name: "Inpatient" },
        { id: "O", name: "Outpatient" },
      ],
      medicalTypes: [
        { id: 2, name: "Dental Cases" },
        { id: 1, name: "Eye Cases" },
      ],
      removedOptions: [
        { id: 0, name: "Valid Cases" },
        { id: 1, name: "Removed Cases" },
      ],
      approvalStatusOptions: [
        { id: "approved", name: "Approved" },
        { id: "pending", name: "Pending" },
        { id: "rejected", name: "Rejected" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      lookups: "cases/lookups",
    }),
  },
  mounted() {
    this.$store.dispatch("cases/cases", { query: {} });
  },
  methods: {
    selectReport(reportType) {
      this.selectedReport = reportType;
      this.resetFilters();
    },
    goBack() {
      this.selectedReport = null;
      this.resetFilters();
    },
    getReportClass(report) {
      const classes = {
        exportMrm: "mrm",
        exportApprovalTracking: "approval",
        exportInvoiceReport: "invoice",
        exportCollectedReport: "collected",
        exportTrendingReport: "trending",
        exportDischargeSummary: "discharge",
        exportEmrReport: "emr",
      };
      return classes[report] || "";
    },
    getReportIcon(report) {
      const icons = {
        exportMrm: "ClipboardIcon",
        exportApprovalTracking: "CheckCircleIcon",
        exportInvoiceReport: "FileTextIcon",
        exportCollectedReport: "DatabaseIcon",
        exportTrendingReport: "TrendingUpIcon",
        exportDischargeSummary: "ActivityIcon",
        exportEmrReport: "MonitorIcon",
      };
      return icons[report] || "FileIcon";
    },
    getReportTitle(report) {
      const titles = {
        exportMrm: "MRM Report",
        exportApprovalTracking: "Approval Tracking Report",
        exportInvoiceReport: "Invoice Report",
        exportCollectedReport: "Collected Report",
        exportTrendingReport: "Trending Report",
        exportDischargeSummary: "Discharge Summary Report",
        exportEmrReport: "EMR Report",
      };
      return titles[report] || "Report";
    },
    resetFilters() {
      this.filter = {};
      this.showValidation = false;
    },
    validateFilters() {
      this.showValidation = true;

      // All reports require Branch and Start/End Date
      if (
        !this.filter.branch_id ||
        !this.filter.start_date ||
        !this.filter.end_date
      ) {
        return false;
      }

      return true;
    },
    exportReport() {
      if (!this.validateFilters()) return;

      this.loading = true;
      this.progress = 0;
      this.showProgressModal = true;

      const query = {
        ...Object.fromEntries(
          Object.entries(this.filter).filter(
            ([key, value]) => value != null && value !== "",
          ),
        ),
        language: this.$i18n.locale,
      };

      console.log("Export Query:", query);
      console.log("Selected Report:", this.selectedReport);
      console.log("Filter:", this.filter);

      // Create cancel token
      this.cancelSource = CancelToken.source();

      const onDownloadProgress = (progressEvent) => {
        if (progressEvent.total) {
          this.progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
        }
      };

      this.$store
        .dispatch(`cases/${this.selectedReport}`, {
          ...query, // Spread query directly
          onDownloadProgress,
          cancelToken: this.cancelSource.token,
        })
        .then(() => {
          this.progress = 100;
          setTimeout(() => {
            this.showProgressModal = false;
            this.loading = false;
            this.progress = 0;
            this.cancelSource = null;
            this.$swal({
              icon: "success",
              title: "Report Generated",
              text: "Your report has been downloaded successfully!",
              showConfirmButton: false,
              timer: 2000,
            });
          }, 500);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            console.log("Request canceled", error.message);
            // Optional: Show specific message for cancellation
          } else {
            console.error("Export Error:", error);
            this.showProgressModal = false;
            this.loading = false;
            this.progress = 0;
            this.cancelSource = null;
            this.$swal({
              icon: "error",
              title: "Generation Failed",
              text: "Failed to generate the report. Please try again.",
              showConfirmButton: true,
            });
          }
        });
    },
    cancelExport() {
      if (this.cancelSource) {
        this.cancelSource.cancel("User cancelled the export.");
      }
      this.showProgressModal = false;
      this.loading = false;
      this.progress = 0;
      this.cancelSource = null;
    },
  },
};
</script>

<style lang="scss" scoped>
// Project Brand Colors
$brand-primary: #054978;
$brand-secondary: #750e38;

.generate-reports-container {
  padding: 1rem;
}

.section-title {
  color: $brand-primary;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.selected-report-card {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #ebe9f1;

  .report-badge {
    width: 44px;
    height: 44px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    &.mrm {
      background: $brand-primary;
    }
    &.approval {
      background: #28c76f;
    }
    &.invoice {
      background: $brand-secondary;
    }
    &.collected {
      background: #00cfe8;
    }
    &.trending {
      background: #ff9f43;
    }
    &.discharge {
      background: #7c3aed;
    }
    &.emr {
      background: #06b6d4;
    }
  }
}

.filters-card {
  border: 1px solid #ebe9f1;
  box-shadow: none;
  border-radius: 8px;

  .form-label {
    font-size: 0.85rem;
    font-weight: 500;
    color: #5e5873;
    margin-bottom: 0.5rem;
    display: block;
  }
}

.export-cards-grid {
  margin-top: 0.5rem;
}

.export-card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #ebe9f1;

  &:hover {
    border-color: $brand-primary;
    box-shadow: 0 4px 12px rgba(5, 73, 120, 0.15);

    .export-card-action {
      background: $brand-primary;
      color: white;
    }
  }

  .export-card-icon {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: white;

    &.mrm {
      background: $brand-primary;
    }

    &.approval {
      background: #28c76f;
    }

    &.invoice {
      background: $brand-secondary;
    }

    &.collected {
      background: #00cfe8;
    }

    &.trending {
      background: #ff9f43;
    }

    &.discharge {
      background: #7c3aed;
    }

    &.emr {
      background: #06b6d4;
    }
  }

  .export-card-content {
    flex: 1;

    h4 {
      margin: 0 0 0.25rem;
      font-size: 0.95rem;
      font-weight: 600;
      color: #5e5873;
    }

    p {
      margin: 0;
      font-size: 0.8rem;
      color: #b9b9c3;
    }
  }

  .export-card-action {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: #f8f8f8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $brand-primary;
    transition: all 0.2s ease;
  }
}

.export-progress-content {
  text-align: center;
  padding: 1rem;

  .export-progress-icon {
    margin-bottom: 1rem;
  }

  p {
    color: #5e5873;
    font-size: 0.95rem;
  }
}

.is-invalid {
  border-color: #ea5455 !important;
}

// Dark mode support
.dark-layout {
  .section-title {
    color: #d0d2d6;
  }

  .selected-report-card {
    background: #283046;
    border-color: #3b4253;
  }

  .filters-card {
    background: #283046;
    border-color: #3b4253;

    .form-label {
      color: #d0d2d6;
    }
  }

  .export-card {
    background: #283046;
    border-color: #3b4253;

    &:hover {
      border-color: $brand-primary;
    }

    .export-card-content {
      h4 {
        color: #d0d2d6;
      }

      p {
        color: #676d7d;
      }
    }

    .export-card-action {
      background: #161d31;
    }
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
